import React from 'react';
import { Bar, Line, Pie, Doughnut } from 'react-chartjs-2';
import { 
  Chart as ChartJS, 
  CategoryScale, 
  LinearScale, 
  BarElement, 
  LineElement, 
  PointElement, 
  ArcElement, 
  Title, 
  Tooltip, 
  Legend,
  Filler
} from 'chart.js';
import { Box, Typography, useTheme } from '@mui/material';

// Register Chart.js components
ChartJS.register(
  CategoryScale, 
  LinearScale, 
  BarElement, 
  LineElement, 
  PointElement, 
  ArcElement, 
  Title, 
  Tooltip, 
  Legend,
  Filler
);

const ChartDisplay = ({ chartData }) => {
  const theme = useTheme();
  
  if (!chartData || !chartData.type || !chartData.data) {
    return (
      <Typography variant="body2" color="error" sx={{ mt: 1, mb: 1 }}>
        Geçersiz grafik verisi
      </Typography>
    );
  }

  const { type, data, options = {} } = chartData;
  
  // Apply theme-aware styling to the chart
  const defaultOptions = {
    responsive: true,
    maintainAspectRatio: true,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          color: theme.palette.text.primary,
          font: {
            family: theme.typography.fontFamily,
            size: 12
          }
        }
      },
      title: {
        display: options.title?.text ? true : false,
        text: options.title?.text || '',
        color: theme.palette.text.primary,
        font: {
          family: theme.typography.fontFamily,
          size: 16,
          weight: 'bold'
        }
      },
      tooltip: {
        backgroundColor: theme.palette.mode === 'dark' 
          ? theme.palette.grey[800] 
          : theme.palette.grey[100],
        titleColor: theme.palette.text.primary,
        bodyColor: theme.palette.text.secondary,
        borderColor: theme.palette.divider,
        borderWidth: 1,
        callbacks: {
          label: function(context) {
            let label = context.dataset.label || '';
            if (label) {
              label += ': ';
            }
            
            const value = context.parsed;
            let displayValue;
            
            // For pie/doughnut charts, show percentage
            if (context.chart.config.type === 'pie' || context.chart.config.type === 'doughnut') {
              const total = context.dataset.data.reduce((sum, val) => sum + val, 0);
              const percentage = Math.round((value * 100) / total);
              displayValue = `${context.formattedValue} (${percentage}%)`;
            } else if (typeof value === 'object') {
              // For bar/line charts
              displayValue = context.formattedValue;
            } else {
              displayValue = context.formattedValue;
            }
            
            return label + displayValue;
          }
        }
      }
    },
    scales: type.toLowerCase() !== 'pie' && type.toLowerCase() !== 'doughnut' ? {
      x: {
        grid: {
          color: theme.palette.divider,
        },
        ticks: {
          color: theme.palette.text.secondary,
          font: {
            family: theme.typography.fontFamily
          }
        }
      },
      y: {
        grid: {
          color: theme.palette.divider,
        },
        ticks: {
          color: theme.palette.text.secondary,
          font: {
            family: theme.typography.fontFamily
          }
        }
      }
    } : undefined
  };

  // Merge user options with default options
  const mergedOptions = {
    ...defaultOptions,
    ...options,
    plugins: {
      ...defaultOptions.plugins,
      ...(options.plugins || {}),
      // Ensure tooltip callbacks are preserved
      tooltip: {
        ...defaultOptions.plugins.tooltip,
        ...(options.plugins?.tooltip || {}),
        callbacks: {
          ...defaultOptions.plugins.tooltip.callbacks,
          ...(options.plugins?.tooltip?.callbacks || {})
        }
      }
    }
  };

  // Apply theme colors to datasets if not specified
  if (data.datasets) {
    const themeColors = [
      theme.palette.primary.main,
      theme.palette.secondary.main,
      theme.palette.success.main,
      theme.palette.info.main,
      theme.palette.warning.main,
      theme.palette.error.main,
    ];
    
    data.datasets = data.datasets.map((dataset, index) => {
      const color = themeColors[index % themeColors.length];
      
      if (type.toLowerCase() === 'line') {
        return {
          borderColor: dataset.borderColor || color,
          backgroundColor: dataset.backgroundColor || `${color}20`,
          pointBackgroundColor: dataset.pointBackgroundColor || color,
          tension: dataset.tension !== undefined ? dataset.tension : 0.4,
          ...dataset
        };
      } else if (type.toLowerCase() === 'bar') {
        return {
          backgroundColor: dataset.backgroundColor || color,
          hoverBackgroundColor: dataset.hoverBackgroundColor || `${color}DD`,
          ...dataset
        };
      } else if (type.toLowerCase() === 'pie' || type.toLowerCase() === 'doughnut') {
        // For pie/doughnut charts, calculate and add percentages to the labels if not already present
        const total = dataset.data.reduce((sum, val) => sum + val, 0);
        
        return {
          backgroundColor: dataset.backgroundColor || 
            data.labels.map((_, i) => themeColors[i % themeColors.length]),
          borderColor: dataset.borderColor || theme.palette.background.paper,
          borderWidth: dataset.borderWidth !== undefined ? dataset.borderWidth : 1,
          ...dataset
        };
      }
      
      return dataset;
    });
  }

  const chartComponents = {
    bar: Bar,
    line: Line,
    pie: Pie,
    doughnut: Doughnut
  };

  const ChartComponent = chartComponents[type.toLowerCase()];
  
  if (!ChartComponent) {
    return (
      <Typography variant="body2" color="error" sx={{ mt: 1, mb: 1 }}>
        Desteklenmeyen grafik türü: {type}
      </Typography>
    );
  }

  return (
    <Box 
      sx={{ 
        mt: 2, 
        mb: 2, 
        width: '100%',
        maxWidth: '500px',
        height: type.toLowerCase() === 'bar' ? '300px' : '250px',
        borderRadius: '12px', 
        boxShadow: theme => `0 2px 8px ${theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.4)' : 'rgba(0,0,0,0.1)'}`,
        p: 2,
        backgroundColor: theme.palette.background.paper,
        transition: 'all 0.3s ease',
        '&:hover': {
          boxShadow: theme => `0 4px 12px ${theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.6)' : 'rgba(0,0,0,0.15)'}`,
          transform: 'translateY(-2px)'
        }
      }}
    >
      <ChartComponent data={data} options={mergedOptions} />
    </Box>
  );
};

export default ChartDisplay;
